import React, { useState } from 'react';
import { X, Check, Crown } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { getStripePublishableKey } from '../../config/stripe';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSchool } from '../../contexts/SchoolContext';
import { useAuth } from '../../contexts/AuthContext';
import { SUBSCRIPTION_PLANS, SubscriptionPlan } from '../../types/subscription';
import toast from 'react-hot-toast';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: SubscriptionPlan | null;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, currentPlan }) => {
  const { loading } = useSubscription();
  const { selectedSchool: school } = useSchool();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedUserCount, setSelectedUserCount] = useState<number>(5);
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  // Update user count when plan changes
  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
    if (plan && (plan.minUsers || 5) > selectedUserCount) {
      setSelectedUserCount(plan.minUsers || 5);
    }
  };

  // Calculate total monthly cost
  const calculateTotalCost = (planId: string, userCount: number) => {
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
    if (!plan || plan.price === 0) return 0;
    return plan.price * userCount;
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    if (!school?.id || !user) {
      toast.error('Missing required information');
      return;
    }

    try {
      setProcessing(true);
      
      // Load Stripe
      const stripe = await loadStripe(getStripePublishableKey());
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      // Get the selected plan details
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      // Use Stripe Checkout with correct parameters for client-side integration
      // Note: Metadata must be passed via success URL since client-side checkout has limitations
      
      // Determine the correct base URL (use current origin which includes port)
      const baseUrl = window.location.origin;
      
      const { error } = await stripe.redirectToCheckout({
        lineItems: [
          {
            price: plan.priceId, // The Price ID from Stripe Dashboard
            quantity: selectedUserCount,
          }
        ],
        mode: 'subscription',
        successUrl: `${baseUrl}/payment/success?session_id={CHECKOUT_SESSION_ID}&school_id=${school.id}&plan_id=${selectedPlan}&user_count=${selectedUserCount}&user_id=${user.uid}`,
        cancelUrl: `${baseUrl}/payment/cancel`,
        customerEmail: user.email || undefined,
        clientReferenceId: school.id, // This will be available in the webhook
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error upgrading plan:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upgrade plan');
    } finally {
      setProcessing(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Upgrade Your Plan</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Plan Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {SUBSCRIPTION_PLANS.filter(plan => plan.id !== 'free').map((plan) => (
                <div
                  key={plan.id}
                  className={`relative border rounded-lg p-6 cursor-pointer transition-all ${
                    selectedPlan === plan.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${plan.isPopular ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center">
                        <Crown className="h-3 w-3 mr-1" />
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                    <div className="mt-2 mb-4">
                      {plan.price === 0 ? (
                        <span className="text-3xl font-bold text-gray-900">Free</span>
                      ) : (
                        <div>
                          <span className="text-2xl font-bold text-gray-900">${plan.price}</span>
                          <span className="text-gray-500">/user/month</span>
                          {selectedPlan === plan.id && (
                            <div className="mt-1">
                              <span className="text-lg font-semibold text-blue-600">
                                ${calculateTotalCost(plan.id, selectedUserCount)}/month total
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      {plan.price === 0 ? 'Up to 50 users' : `Starting at ${plan.minUsers || 5} users`}
                    </p>
                  </div>

                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-700">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {selectedPlan === plan.id && (
                    <div className="absolute -top-1 -right-1">
                      <div className="bg-blue-500 text-white rounded-full p-1">
                        <Check className="h-4 w-4" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* User Count Selection */}
          {selectedPlan && SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.price !== 0 && (
            <div className="mb-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Number of Users</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Minimum: {SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.minUsers || 5} users
                    </span>
                    <span className="text-sm text-gray-600">
                      Selected: {selectedUserCount} users
                    </span>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="range"
                      min={SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.minUsers || 5}
                      max="500"
                      value={selectedUserCount}
                      onChange={(e) => setSelectedUserCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.minUsers || 5}</span>
                      <span>500+</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm text-gray-600 mb-1">Or enter exact number:</label>
                      <input
                        type="number"
                        min={SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.minUsers || 5}
                        max="10000"
                        value={selectedUserCount}
                        onChange={(e) => setSelectedUserCount(Math.max(
                          SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.minUsers || 5,
                          parseInt(e.target.value) || 5
                        ))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter number of users"
                      />
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Monthly total:</div>
                      <div className="text-xl font-bold text-blue-600">
                        ${calculateTotalCost(selectedPlan, selectedUserCount)}/month
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Billing Summary */}
          {selectedPlan && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.name} Plan
                  </span>
                  <span className="font-medium">
                    ${SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.price || 0} × {selectedUserCount} users
                  </span>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total Monthly</span>
                    <span>${calculateTotalCost(selectedPlan, selectedUserCount)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            You can change or cancel your plan at any time.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpgrade}
              disabled={!selectedPlan || loading || processing}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? 'Creating checkout...' : loading ? 'Processing...' : 'Upgrade Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;